import { ASSISTENT_API_URL } from "@/config/backend";
import { useAuthStore } from "@/stores/auth-store";
import { useCustomerStore } from "@/stores/customer-store";
import { useDevicesStore } from "@/stores/devices-store";
import axios from "axios";

// Create axios instance for React components
export const api = axios.create({
  baseURL: ASSISTENT_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth headers
api.interceptors.request.use((config) => {
  const authStore = useAuthStore.getState();
  if (authStore?.getApiHeaders) {
    const headers = authStore.getApiHeaders();
    Object.assign(config.headers, headers);
  }
  return config;
});

// Add response interceptor for better error handling in dev mode
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const isDevBypass = localStorage.getItem('dev_bypass_mode') === 'true';

    if (isDevBypass && error.response?.status === 401) {
      // In dev bypass mode, ignore 401 errors
      console.log('🛠️ Dev Bypass: Ignoring 401 error');
      return Promise.resolve({ data: null });
    }

    return Promise.reject(error);
  }
);

// Helper functions to get store data
const getAuthHeaders = () => {
  const authStore = useAuthStore.getState();
  return authStore.getApiHeaders();
};

const getCurrentDeviceSerialId = () => {
  const devicesStore = useDevicesStore.getState();
  return devicesStore.currentDeviceSerialId;
};

/**
 * Fetches the schedules for the current device.
 */
export const getAllProfiles = async () => {
  const urlProfiles = `${ASSISTENT_API_URL}device/${getCurrentDeviceSerialId()}/callhandling/profiles`;

  return axios
    .get(urlProfiles, {
      headers: getAuthHeaders()
    })
    .then((response) => {
      const scheduleList = response.data.resultObject.profiles;
      return scheduleList.map((profile: any) => ({
        ...profile,
        // TODO: Add default messages when utils are migrated
      }));
    })
    .catch((error) => {
      console.error("Error fetching profiles:", error);
      return [];
    });
};

/**
 * Fetches a specific profile by ID
 */
export const getProfile = async (profileId: string) => {
  const urlProfile = `${ASSISTENT_API_URL}device/${getCurrentDeviceSerialId()}/callhandling/profile/${profileId}`;

  return axios
    .get(urlProfile, {
      headers: getAuthHeaders()
    })
    .then((response) => {
      return response.data.resultObject;
    })
    .catch((error) => {
      console.error("Error fetching profile:", error);
      const customerStore = useCustomerStore.getState();
      customerStore.resetCallhandlingProfiles?.();
      throw error;
    });
};

/**
 * Deletes a profile by ID
 */
export const deleteProfile = async (profileId: string) => {
  const url = `${ASSISTENT_API_URL}device/${getCurrentDeviceSerialId()}/callhandling/profile/${profileId}`;

  return axios
    .delete(url, {
      headers: getAuthHeaders()
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error("Error deleting profile:", error);
      throw error;
    });
};

/**
 * Activates or deactivates a profile
 */
export const setProfileActivation = async (profileId: string, isActive: boolean) => {
  const url = `${ASSISTENT_API_URL}device/${getCurrentDeviceSerialId()}/callhandling/${profileId}/activation/${isActive}`;

  return axios
    .put(url, {}, {
      headers: getAuthHeaders()
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error("Error setting profile activation:", error);
      throw error;
    });
};

/**
 * Creates a new profile
 */
export const createProfile = async (profileData: any) => {
  const urlCreateProfile = `${ASSISTENT_API_URL}device/${getCurrentDeviceSerialId()}/callhandling/profile`;

  return axios
    .post(urlCreateProfile, profileData, {
      headers: getAuthHeaders()
    })
    .then((response) => {
      const customerStore = useCustomerStore.getState();
      customerStore.resetCallhandlingProfiles?.();
      return response.data;
    })
    .catch((error) => {
      console.error("Error creating profile:", error);
      throw error;
    });
};

/**
 * Updates an existing profile
 */
export const updateProfile = async (profileId: string, profileData: any) => {
  const urlUpdateProfile = `${ASSISTENT_API_URL}device/${getCurrentDeviceSerialId()}/callhandling/profile/${profileId}`;

  return axios
    .put(urlUpdateProfile, profileData, {
      headers: getAuthHeaders(),
      timeout: 30000
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error("Error updating profile:", error);
      throw error;
    });
};

/**
 * Fetches call routing data
 */
export const getCallRouting = async (profileId: string) => {
  const urlCallRouting = `${ASSISTENT_API_URL}device/${getCurrentDeviceSerialId()}/callhandling/profile/${profileId}/callrouting`;

  return axios
    .get(urlCallRouting, {
      headers: getAuthHeaders()
    })
    .then((response) => {
      return response.data.resultObject;
    })
    .catch((error) => {
      console.error("Error fetching call routing:", error);
      throw error;
    });
};

/**
 * Updates call routing data
 */
export const updateCallRouting = async (profileId: string, routingData: any) => {
  const urlUpdateCallRouting = `${ASSISTENT_API_URL}device/${getCurrentDeviceSerialId()}/callhandling/profile/${profileId}/callrouting`;

  return axios
    .put(urlUpdateCallRouting, routingData, {
      headers: getAuthHeaders()
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error("Error updating call routing:", error);
      throw error;
    });
};

/**
 * Fetches generic device configuration
 */
export const getGenericDeviceConfiguration = async () => {
  const url = `${ASSISTENT_API_URL}configuration/basic/${getCurrentDeviceSerialId()}/DEVICEID_SELECTOR`;

  return axios
    .get(url, {
      headers: getAuthHeaders()
    })
    .then((response) => {
      return response.data.resultObject;
    })
    .catch((error) => {
      console.error("Error fetching device configuration:", error);
      return {
        errorCode: error.response?.data?.errorCode || 'error.unknown'
      };
    });
};

/**
 * Updates call routing configuration
 */
export const updateCallRoutingConfiguration = async (value: string) => {
  const url = `${ASSISTENT_API_URL}configuration/basic/${getCurrentDeviceSerialId()}`;

  const requestBody = {
    key: "DEVICEID_SELECTOR",
    value: value
  };

  try {
    const response = await axios.put(url, requestBody, {
      headers: getAuthHeaders()
    });

    if (response.data.errorCode !== null) {
      throw new Error(response.data.errorCode);
    }

    return response;
  } catch (error) {
    console.error("Error saving device configuration:", error);
    return {
      errorCode: (error as any).response?.data?.errorCode || 'error.unknown'
    };
  }
};

export default api;